import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const TVADropdown = ({ 
  tvaRates, 
  tvaId, 
  isTvaDropdownOpen, 
  setIsTvaDropdownOpen,
  tvaSearchTerm,
  setTvaSearchTerm,
  filteredTvaRates,
  dropdownPosition,
  setTvaId,
  setDropdownPosition
}) => {
  const tvaDropdownRef = useRef(null);

  const clearSearchTerm = (e) => {
    e.stopPropagation();
    setTvaSearchTerm('');
  };

  const handleSelectTva = (id) => {
    setTvaId(id);
    setTimeout(() => {
      setIsTvaDropdownOpen(false);
      setTvaSearchTerm('');
    }, 100);
  };

  const handleClearTva = (e) => {
    e.stopPropagation();
    setTvaId(null);
  };

  useEffect(() => {
      const updatePosition = () => {
        if (tvaDropdownRef.current && isTvaDropdownOpen) {
          const rect = tvaDropdownRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width
          });
        }
      };
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [isTvaDropdownOpen]);

  return (
    <div className="sm:col-span-2">
      <label htmlFor="tva" className="block text-sm font-medium text-gray-700 mb-1">
        Taux de TVA <span className="text-red-500">*</span>
      </label>
      <div className="relative" ref={tvaDropdownRef}>
        <button
          type="button"
          className="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
          onClick={() => setIsTvaDropdownOpen(!isTvaDropdownOpen)}
        >
          <span className="block truncate">
            {tvaId && tvaRates.find(t => t.id.toString() === tvaId.toString())
              ? `${tvaRates.find(t => t.id.toString() === tvaId.toString()).taux}%`
              : 'Sélectionner un taux'}
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M10 17a1 1 0 01-.707-.293l-3-3a1 1 0 011.414-1.414L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3A1 1 0 0110 17z" clipRule="evenodd" />
            </svg>
          </span>
        </button>

        {tvaId && (
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-8 z-10 cursor-pointer"
            onClick={handleClearTva}
          >
            <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        {isTvaDropdownOpen && createPortal(
          <div
            className="fixed z-50 bg-white shadow-lg rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            style={{
              width: dropdownPosition.width,
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '320px'
            }}
          >
            <div className="sticky top-0 z-10 bg-white">
              <input
                type="text"
                className="block w-full px-4 py-2 text-sm border-0 focus:ring-0 focus:outline-none"
                placeholder="Rechercher un taux de TVA..."
                value={tvaSearchTerm}
                onChange={(e) => setTvaSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{ paddingRight: "36px" }}
              />
              {tvaSearchTerm && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-500 bg-white p-1 rounded-full"
                  onClick={clearSearchTerm}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <div className="h-px bg-gray-200" />
            </div>

            <div className="max-h-52 overflow-y-auto">
              {filteredTvaRates.length === 0 ? (
                <div className="py-2 px-3 text-sm text-gray-500">Aucun taux de TVA trouvé</div>
              ) : (
                filteredTvaRates.map((tva) => (
                  <div
                    key={tva.id}
                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${tvaId === tva.id ? 'bg-[#174540] bg-opacity-10 font-medium' : ''}`}
                    onClick={() => handleSelectTva(tva.id)}
                  >
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900">{tva.taux}%</span>
                    </div>

                    {tvaId === tva.id && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#174540]">
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default TVADropdown;